import { useState, useCallback } from 'react';
import AuthService from '../services/authService';
import VTOAuthHandler from '../services/VTOAuthHandler';

/**
 * Custom hook for handling VTO authentication flows
 */
const useVTOAuthentication = () => {
  const [showLoginConfirmation, setShowLoginConfirmation] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);
  
  const authService = new AuthService();
  const vtoAuthHandler = new VTOAuthHandler();

  /**
   * Check if user is authenticated for VTO operations
   * @returns {Promise<boolean>} True if authenticated, false otherwise
   */
  const checkAuthentication = useCallback(async () => {
    setIsCheckingAuth(true);
    
    try {
      const authStatus = await vtoAuthHandler.checkAuthenticationStatus();
      return authStatus.authenticated;
    } catch (error) {
      console.error('VTO Authentication check failed:', error);
      return false;
    } finally {
      setIsCheckingAuth(false);
    }
  }, [vtoAuthHandler]);

  /**
   * Handle VTO access request - checks auth and shows appropriate portal
   * @param {Function} onAuthenticatedCallback - Callback to execute if user is authenticated
   * @returns {Promise<boolean>} True if user can proceed, false if auth is required
   */
  const handleVTOAccess = useCallback(async (onAuthenticatedCallback) => {
    const isAuthenticated = await checkAuthentication();
    
    if (isAuthenticated) {
      // User is authenticated, proceed with VTO
      if (onAuthenticatedCallback) {
        onAuthenticatedCallback();
      }
      return true;
    } else {
      // User is not authenticated, show login confirmation
      setShowLoginConfirmation(true);
      return false;
    }
  }, [checkAuthentication]);

  /**
   * Handle login confirmation - redirect to SHEYOU authentication
   */
  const handleLoginConfirmation = useCallback(async () => {
    try {
      setShowLoginConfirmation(false);
      // Reload the page to pick up the new auth state after inline login
      window.location.reload();
    } catch (error) {
      console.error('VTO login confirmation failed:', error);
    }
  }, []);

  /**
   * Handle login confirmation cancellation
   */
  const handleLoginCancel = useCallback(() => {
    setShowLoginConfirmation(false);
  }, []);

  /**
   * Check for authentication return and handle VTO context restoration
   */
  const handleAuthReturn = useCallback(async () => {
    try {
      // Check if we're returning from authentication
      const restorationCheck = vtoAuthHandler.checkForVTORestoration();
      
      if (restorationCheck.shouldRestore && restorationCheck.isValid) {
        console.log('VTO: Handling authentication return');
        
        // Get token from URL parameters (this would be handled by the auth callback)
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');
        
        if (token) {
          const callbackResult = await vtoAuthHandler.handleAuthCallback(token);
          
          if (callbackResult.success && callbackResult.contextRestoration.success) {
            // Authentication successful and context restored
            const context = callbackResult.contextRestoration.context;
            
            if (context.operation.type === 'virtual_try_on_access') {
              // Clean up URL parameters
              vtoAuthHandler.cleanupUrlParameters();
              
              // Return success - the calling component should open the person image portal
              return {
                success: true,
                shouldOpenPersonImagePortal: true,
                context: context
              };
            }
          }
        }
      }
      
      return { success: false };
      
    } catch (error) {
      console.error('VTO auth return handling failed:', error);
      return { success: false, error: error.message };
    }
  }, [vtoAuthHandler]);

  return {
    // State
    showLoginConfirmation,
    isCheckingAuth,
    
    // Methods
    checkAuthentication,
    handleVTOAccess,
    handleLoginConfirmation,
    handleLoginCancel,
    handleAuthReturn,
    
    // Utilities
    isAuthenticated: authService.isAuthenticated(),
    authStatus: authService.getAuthStatus()
  };
};

export default useVTOAuthentication;